import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface WellSurveyState {
    loading: boolean;
    error: string | null;
    wellSurveyData: any;
    isWellSurveyDataPersisted: boolean;
    KOPOverride: number | null,
    MaxDLSAboveKOP: number | null,
    DepthOfMaxDLS: number | null,
    TransitionZone: number | null;
    TailPipeLength: number | null;
    TailPipeBottom: number | null;
    TailPipeInsideTZ: number | null;
    wellSurvey: {
        notes: string | null;
    },
    ESPLength: number;
    showWellDataInMainReport: boolean;
    BottomOfESPSensor: number;
    TailPipeDesander: number;
};

export const initialState: WellSurveyState = {
    loading: false,
    error: null,
    wellSurveyData: [],
    isWellSurveyDataPersisted: false,
    KOPOverride: null,
    MaxDLSAboveKOP: null,
    DepthOfMaxDLS: null,
    TransitionZone: null,
    TailPipeLength: null,
    TailPipeBottom: null,
    TailPipeInsideTZ: null,
    wellSurvey: {
        notes: " "
    },
    ESPLength: 0,
    showWellDataInMainReport: false,
    BottomOfESPSensor: 0,
    TailPipeDesander: 0,
};

const wellSurveySlice = createSlice({
    name: "wellSurvey",
    initialState,
    reducers: {
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        setWellSurveyData: (state, action: PayloadAction<any>) => {
            state.wellSurveyData = action.payload;
        },
        setWellKOPOverride: (state, action: PayloadAction<any>) => {
            state.KOPOverride = action.payload;
        },
        setMaxDLSAboveKOP: (state, action: PayloadAction<any>) => {
            state.MaxDLSAboveKOP = action.payload;
        },
        setDepthOfMaxDLS: (state, action: PayloadAction<any>) => {
            state.DepthOfMaxDLS = action.payload;
        },
        setTransitionZone: (state, action: PayloadAction<number>) => {
            state.TransitionZone = action.payload;
        },
        setTailPipeLength: (state, action: PayloadAction<number>) => {
            state.TailPipeLength = action.payload;
        },
        setTailPipeBottom: (state, action: PayloadAction<number>) => {
            state.TailPipeBottom = action.payload;
        },
        setTailPipeInsideTZ: (state, action: PayloadAction<number>) => {
            state.TailPipeInsideTZ = action.payload;
        },

        setNotes: (state, action) => {
            state.wellSurvey = {
                notes: action.payload
            };
        },
        setWellDataInMainReportFlag: (state, action) => {
            state.showWellDataInMainReport = action.payload;
        },
        fetchExistingData: (state) => {
            state.loading = true;
            state.error = null;
        },

        fetchExistingDataSuccess: (state, action: PayloadAction<any[]>) => {
            state.loading = false;
            state.wellSurveyData = action.payload.map((item, index) => ({
                ...item,
                id: item.id || index + 1
            }));
        },

        fetchExistingDataError: (state, action: PayloadAction<string>) => {
            state.loading = false;
            state.error = action.payload;
        },
        setEspLength: (state, action: PayloadAction<number>) => {
            state.ESPLength = action.payload;
        },
        setBottomEspSensor: (state, action: PayloadAction<number>) => {
            state.BottomOfESPSensor = action.payload;
        },
        setTailPipeDesander: (state, action: PayloadAction<number>) => {
            state.TailPipeDesander = action.payload;
        },

        setWellSurveyDataPersisted: (state, action: PayloadAction<boolean>) => {
            state.isWellSurveyDataPersisted = action.payload;
        },

        clearDataSuccess: (state) => {
            state.wellSurveyData = [];
            state.isWellSurveyDataPersisted = false;
            state.KOPOverride = null;
            state.MaxDLSAboveKOP = null;
            state.DepthOfMaxDLS = null;
            state.TransitionZone = null;
            state.TailPipeLength = null;
            state.TailPipeBottom = null;
            state.TailPipeInsideTZ = null;
            state.showWellDataInMainReport = false;
            state.wellSurvey = {
                notes: " "
            };
        },

        resetWellSurveyState: () => initialState,

        // JetLift specific actions
        "jetlift/wellSurvey/fetchExistingDataSuccess": (state, action: PayloadAction<any[]>) => {
            state.loading = false;
            state.wellSurveyData = action.payload.map((item, index) => ({
                ...item,
                id: item.id || index + 1
            }));
        },

        "jetlift/wellSurvey/fetchExistingDataError": (state, action: PayloadAction<string>) => {
            state.loading = false;
            state.error = action.payload;
        },

        "jetlift/wellSurvey/setWellSurveyDataPersisted": (state, action: PayloadAction<boolean>) => {
            state.isWellSurveyDataPersisted = action.payload;
        },

        "jetlift/wellSurvey/fetchDataSuccess": (state, action: PayloadAction<any[]>) => {
            state.loading = false;
            state.wellSurveyData = action.payload;
            // state.isWellSurveyDataPersisted = true;
            state.error = null;
        },

        "jetlift/wellSurvey/clearExistingDataError": (state, action: PayloadAction<string>) => {
            state.loading = false;
            state.error = action.payload;
        },

    },
});

export const { setTailPipeDesander, setBottomEspSensor, setEspLength, setLoading, setError, setWellSurveyData, resetWellSurveyState, setWellKOPOverride, setMaxDLSAboveKOP, setDepthOfMaxDLS, setTransitionZone, setTailPipeLength, setTailPipeBottom, setTailPipeInsideTZ, setNotes, fetchExistingData, fetchExistingDataSuccess, fetchExistingDataError, setWellDataInMainReportFlag, setWellSurveyDataPersisted, clearDataSuccess } = wellSurveySlice.actions;

// Enhanced reducer to handle epic actions
const wellSurveyReducer = (state: WellSurveyState = initialState, action: any) => {
    // Handle RTK slice actions first
    const sliceState = wellSurveySlice.reducer(state, action);
    
    // Handle epic actions
    switch (action.type) {
        case 'jetlift/wellSurvey/setWellKOPOverride':
            return {
                ...sliceState,
                KOPOverride: action.payload
            };
        case 'jetlift/wellSurvey/setWellSurveyDataPersisted':
            return {
                ...sliceState,
                isWellSurveyDataPersisted: action.payload
            };
        case 'jetlift/wellSurvey/fetchData':
            return {
                ...sliceState,
                loading: true,
                error: null
            };
        case 'jetlift/wellSurvey/fetchDataSuccess':
            return {
                ...sliceState,
                loading: false,
                wellSurveyData: action.payload,
                // isWellSurveyDataPersisted: true
            };
        case 'jetlift/wellSurvey/clearData':
            return {
                ...sliceState,
                loading: true
            };
        case 'jetlift/wellSurvey/clearDataSuccess':
            return {
                ...sliceState,
                loading: false,
                wellSurveyData: [],
                isWellSurveyDataPersisted: false,
                KOPOverride: null,
                MaxDLSAboveKOP: null,
                DepthOfMaxDLS: null,
                TransitionZone: null,
                TailPipeLength: null,
                TailPipeBottom: null,
                TailPipeInsideTZ: null,
                showWellDataInMainReport: false,
                wellSurvey: {
                    notes: " "
                }
            };
        case 'jetlift/wellSurvey/fetchDataError':
            return {
                ...sliceState,
                loading: false,
                error: action.payload
            };
        case 'jetlift/wellSurvey/fetchExistingDataSuccess':
            return {
                ...sliceState,
                loading: false,
                wellSurveyData: action.payload,
                error: null
            };
        case 'jetlift/wellSurvey/fetchExistingDataError':
            return {
                ...sliceState,
                loading: false,
                error: action.payload
            };
        case 'jetlift/wellSurvey/saveData':
            return {
                ...sliceState,
                loading: true,
                error: null
            };
        case 'jetlift/wellSurvey/saveDataSuccess':
            return {
                ...sliceState,
                loading: false,
                error: null
            };
        case 'jetlift/wellSurvey/saveDataError':
            return {
                ...sliceState,
                loading: false,
                error: action.payload
            };
        default:
            return sliceState;
    }
};

export default wellSurveyReducer;
